import React from 'react';
import { PageView } from '../types';

interface WhyMusePageProps {
  setActivePage: (page: PageView) => void;
  onOpenLeadModal: () => void;
}

export const WhyMusePage: React.FC<WhyMusePageProps> = ({ onOpenLeadModal }) => {
  return (
    <div className="why-minimal">
      {/* HERO HEADER */}
      <section className="why-hero-center">
        <div className="section-container">
          <h1 className="w-title">Operating the Infrastructure That Powers the Internet</h1>
          <p className="w-sub">
            MUSE Telemetry was founded on a simple, uncompromising conviction: network observability should be vendor-independent, technically transparent, and built to the standards of a critical public utility.
          </p>
        </div>
      </section>

      {/* UTILITY GRADE PHILOSOPHY: SPACIOUS 2-COLUMN GRID */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="section-heading">
            <h2>The Philosophy of Utility-Grade Observability</h2>
            <p>When an ISP or regional fiber network goes down, real lives and businesses pause. Up-time is non-negotiable.</p>
          </div>

          <div className="grid-2">
            <div className="flat-card">
              <div className="card-header-dot">
                <span className="status-dot green" />
                <span className="card-tag">PILLAR 01</span>
              </div>
              <h3>Always Available</h3>
              <p>Built on zero single points of failure with a cloud-native, serverless ingestion pipeline designed for continuous operation.</p>
            </div>

            <div className="flat-card">
              <div className="card-header-dot">
                <span className="status-dot blue" />
                <span className="card-tag">PILLAR 02</span>
              </div>
              <h3>Always Watching</h3>
              <p>Continuous multi-protocol polling across edge, aggregation, and core layers without requiring proprietary host agents.</p>
            </div>

            <div className="flat-card">
              <div className="card-header-dot">
                <span className="status-dot amber" />
                <span className="card-tag">PILLAR 03</span>
              </div>
              <h3>Always Learning</h3>
              <p>Adaptive baseline profiling catches behavioral telemetry deviations before outages impact customer SLA commitments.</p>
            </div>

            <div className="flat-card">
              <div className="card-header-dot">
                <span className="status-dot green" />
                <span className="card-tag">PILLAR 04 &amp; 05</span>
              </div>
              <h3>Always Improving &amp; Customer-Aware</h3>
              <p>Rapid device onboarding via MUSE Forge, mapping every single operational event directly to subscriber experience.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PEDIGREE GRID: 2-COLUMN GRID */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="section-heading">
            <h2>30+ Years Industry Pedigree</h2>
            <p>Our engineering leadership spans foundational roles at institutions that scaled the internet.</p>
          </div>

          <div className="grid-2">
            <div className="flat-card">
              <span className="ped-tag">AOL / AMERICA ONLINE</span>
              <h3>Nationwide Dial-Up &amp; Broadband Ingestion</h3>
              <p>Managing nationwide dial-up/DSL ingestion and scaling core network infrastructure through the early explosive expansion of consumer internet traffic.</p>
            </div>

            <div className="flat-card">
              <span className="ped-tag">EQUIFAX &amp; IXI</span>
              <h3>High-Concurrency Enterprise Infrastructure</h3>
              <p>Architecting high-security, high-concurrency enterprise data systems with strict regulatory compliance and high-availability standards.</p>
            </div>

            <div className="flat-card">
              <span className="ped-tag">GOOGLE / MANDIANT</span>
              <h3>Cloud-Native Analytics &amp; Security</h3>
              <p>Building cloud-native threat analytics pipelines and defending global enterprise infrastructure against complex operational threats.</p>
            </div>

            <div className="flat-card">
              <span className="ped-tag">MUSE TELEMETRY</span>
              <h3>Engineered by Operators, for Operators</h3>
              <p>We built MUSE Telemetry because we spent 30 years living your exact operational nightmare during 3:00 AM core routing failures.</p>
            </div>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="section-padding border-top">
        <div className="section-container">
          <div className="flat-card cta-card-center">
            <h2>The Vendor-Independence Manifesto</h2>
            <p className="cta-lead">
              1. You own your hardware strategy. Never upgrade working boxes just for software monitoring.  
              <br />
              2. Your telemetry data belongs to you. Stream it freely via open APIs.  
              <br />
              3. Zero gimmick marketing speak. Real software for real network engineers.
            </p>
            <div className="cta-actions">
              <button onClick={onOpenLeadModal} className="btn btn-primary">
                Schedule a Technical Deep Dive
              </button>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .why-hero-center {
          padding: 6rem 0 4rem;
          text-align: center;
          max-width: 800px;
          margin: 0 auto;
        }

        .w-title {
          font-size: 2.8rem;
          font-weight: 800;
          color: #ffffff;
          letter-spacing: -0.03em;
          margin-bottom: 1rem;
        }

        .w-sub {
          font-size: 1.1rem;
          color: var(--text-muted);
          line-height: 1.6;
        }

        .ped-tag {
          font-family: var(--font-mono);
          font-size: 0.72rem;
          color: var(--text-dim);
          display: block;
          margin-bottom: 0.5rem;
        }

        @media (max-width: 800px) {
          .w-title { font-size: 2rem; }
        }
      `}</style>
    </div>
  );
};
